import React from 'react';
import { Modal, Spin } from 'antd';
import { getHomepageConfigStatus, initHomepageDefaultConfig } from '@/api/newApi/homePage';
import { clearHomePageConfigCache } from '@/util/homepageConfig';
import {
  clearHomepageInitPending,
  clearHomepageStatusFailedInCurrentLogin,
  hasHandledHomepageInitInCurrentLogin,
  hasHomepageStatusFailedInCurrentLogin,
  isHomepageInitPending,
  markHomepageInitHandledForCurrentLogin,
  markHomepageInitPending,
  markHomepageStatusFailedInCurrentLogin,
} from '@/util/homepageInitState';

let homepageInitModal: ReturnType<typeof Modal.info> | null = null;
let homepageInitPromise: Promise<boolean> | null = null;

const showHomepageInitLoading = () => {
  if (homepageInitModal) {
    return;
  }
  homepageInitModal = Modal.info({
    title: null,
    closable: false,
    maskClosable: false,
    centered: true,
    icon: null,
    okButtonProps: {
      style: { display: 'none' },
    },
    content: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 0' }}>
        <Spin size="small" />
        <span>{TLT('页面正在初始化，请稍等')}</span>
      </div>
    ),
  });
};

const hideHomepageInitLoading = () => {
  homepageInitModal?.destroy();
  homepageInitModal = null;
};

const initializeHomepageConfig = async (): Promise<boolean> => {
  if (hasHomepageStatusFailedInCurrentLogin()) {
    return false;
  }

  if (hasHandledHomepageInitInCurrentLogin() && !isHomepageInitPending()) {
    return true;
  }

  let statusResponse: any;
  try {
    statusResponse = await getHomepageConfigStatus();
  } catch (error) {
    markHomepageStatusFailedInCurrentLogin();
    console.error('查询首页配置状态失败:', error);
    return false;
  }

  if (!statusResponse?.success) {
    markHomepageStatusFailedInCurrentLogin();
    console.error('查询首页配置状态失败:', statusResponse?.msg);
    return false;
  }

  if (statusResponse?.value !== 0) {
    clearHomepageStatusFailedInCurrentLogin();
    markHomepageInitHandledForCurrentLogin();
    return true;
  }

  showHomepageInitLoading();
  try {
    markHomepageInitPending();
    const initResponse: any = await initHomepageDefaultConfig();
    if (!initResponse?.success || initResponse?.value !== true) {
      throw new Error(initResponse?.msg || '初始化首页配置失败');
    }
    clearHomePageConfigCache();
    clearHomepageStatusFailedInCurrentLogin();
    markHomepageInitHandledForCurrentLogin();
    return true;
  } catch (error) {
    // 初始化失败后保留重试资格，并用内存退避避免每次进首页立即重复请求。
    markHomepageStatusFailedInCurrentLogin();
    console.error('初始化首页配置失败:', error);
    return false;
  } finally {
    clearHomepageInitPending();
    hideHomepageInitLoading();
  }
};

/**
 * 确保当前登录客户已具备可用的首页 CMS 配置。并发入口复用同一 Promise，
 * 避免首次登录时重复初始化并让 CMS 请求读取到未完成的配置。失败返回 false，
 * 调用方仍可尝试读取已有 CMS 配置，不能因初始化故障直接进入空白页。
 */
export function ensureHomepageConfig(): Promise<boolean> {
  if (!homepageInitPromise) {
    homepageInitPromise = initializeHomepageConfig().finally(() => {
      homepageInitPromise = null;
    });
  }
  return homepageInitPromise;
}
